import { ReactNode, useMemo } from 'react';
import { Theme, ThemeProvider, createTheme } from '@mui/material/styles';
import ScopedCssBaseline from '@mui/material/ScopedCssBaseline';
import GlobalStyles from '@mui/material/GlobalStyles';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { I18nProvider } from '@lingui/react';
import { i18n } from '@lingui/core';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { theme } from '@/lib/theme';
import { DataGridLocalizationProvider } from '@/lib/gridLocalization';
import { activateUserLocale } from '@/i18n';
import { AuthProvider, useUser } from '@/lib/firebase-auth';
import { AxiosProvider } from '@/api/provider';
import { ServerInfoProvider } from '@/contexts/ServerInfoContext';
import { shouldRetryQuery } from '@/lib/query';

let runtime: Promise<void> | null = null;

/** Activates the Lingui catalogue once per page load, however many islands mount. */
export function ensureIslandRuntime(): Promise<void> {
  if (!runtime) {
    runtime = activateUserLocale();
  }

  return runtime;
}

// Shared by every island
const queryClient = new QueryClient({ defaultOptions: { queries: { retry: shouldRetryQuery } } });

/**
 * Until Firebase restores the session `useUser()` has no token, so an island that fetches early
 * gets a 401 — never retried, so it stays empty for the life of the page. Signing out also sets
 * `clientInitialized`, so this cannot hang. Next pages get this from `withUser()`.
 */
function WhenSessionRestored({ children }: { children: ReactNode }) {
  return useUser().clientInitialized ? <>{children}</> : null;
}

/**
 * Undoes the host's styling of bare form elements.
 *
 * `ScopedCssBaseline` applies MUI's baseline inside the island; it does not *remove* what the host
 * already declares. materialize.css styles `input`/`textarea` globally — a grey border-bottom, a
 * 3rem height, a bottom margin, a focus box-shadow — by element selector, so those rules reach
 * straight into MUI's fields and win over nothing. Any island with a text field inherits a stray
 * underline; the two islands that shipped first were a button and a header, so it never showed.
 *
 * Only the properties the host actually sets are reset, and only where MUI leaves them unset —
 * that is the whole leak surface. Where MUI *does* declare one and the host outranks it, the value
 * has to be put back rather than cleared: see `OUTLINED_PADDING` below.
 */
const FIELD_RESET = {
  // Longhand, not `borderBottom`/`outline`: jsdom does not expand shorthands, so a test cannot
  // see them — and the style alone is what has to go, whatever width or colour the host chose.
  borderBottomStyle: 'none',
  borderRadius: 0,
  outlineStyle: 'none',
  // MUI's own value, not `auto`: the host forces 45px, and `auto` merely hands the decision to the
  // control's intrinsic height — which for `type=date` is taller than a text input, so a date
  // condition in the filter panel came out 2px taller than the selects beside it.
  height: '1.4375em',
  margin: 0,
  boxShadow: 'none',
  backgroundColor: 'transparent',
  transition: 'none',
};

/**
 * Puts back the padding the host takes away.
 *
 * Its blanket rule sets `padding: 0` on `input[type=text]` and friends, and that selector list
 * outranks `.MuiInputBase-input` — so a field collapses to the height of its own text.
 *
 * **Every selector here starts with `input`, deliberately.** The host's rule cannot match a `div`,
 * so a `Select` — which renders its value into `div.MuiSelect-select`, carrying the same
 * `.MuiInput-input` class — keeps MUI's padding untouched. That asymmetry *is* the bug: a text
 * filter came out 9px shorter than the select beside it (22px against 31px, exactly the `4px + 5px`
 * of a standard input). Restoring the padding on the class alone would fix the height and break the
 * selects instead, overwriting the 24px they reserve for the dropdown arrow.
 *
 * MUI's numbers are restated because CSS cannot re-assert another rule's declaration, only outbid
 * it. Safe to hardcode in the sense that being wrong is *visible* — it shows up as exactly the
 * misalignment this fixes.
 */
const INPUT_PADDING = {
  // Outlined: what the filter panel's fields use.
  'input.MuiOutlinedInput-input': { padding: '16.5px 14px' },
  'input.MuiOutlinedInput-input.MuiInputBase-inputSizeSmall': { padding: '8.5px 14px' },
  // Standard: what the column filter row uses.
  'input.MuiInput-input': { padding: '4px 0 5px' },
  'input.MuiInput-input.MuiInputBase-inputSizeSmall': { padding: '1px 0 5px' },
};

const INPUT_FOCUSED_BORDER = {
  "input[type='text']:focus:not([readonly]),input[type='date']:focus:not([readonly])": {
    borderBottom: 'none',
    boxShadow: 'none',
  },
};

/**
 * Puts the input back under the tick box it is drawn inside.
 *
 * materialize.css parks every `[type=checkbox]`/`[type=radio]` at `left: -9999px`, because it draws
 * its own control in a sibling label. MUI draws the control *around* the input and leaves the real
 * one on top at `opacity: 0` — that transparent input is what a click lands on. Pushed off-screen,
 * the visible tick box has nothing under it and a click does nothing at all.
 *
 * Only `left` is restated: the host's other two declarations, `position: absolute` and
 * `opacity: 0`, are what MUI asks for anyway. Its selector is an attribute plus a pseudo-class, so
 * a reset on the bare element would lose the cascade to it whatever the order — hence the same
 * shape here, which under a scope outranks it.
 */
const TICK_BOX_OFFSET = {
  // `height` too: `FIELD_RESET` cuts every input to a text line, 1.4375em, which left
  // the lower part of a checkbox, and all but the top of a switch's knob, with nothing to click.
  "input[type='checkbox']:not(:checked), input[type='checkbox']:checked": {
    left: 0,
    height: '100%',
  },
  "input[type='radio']:not(:checked), input[type='radio']:checked": { left: 0, height: '100%' },
  // A switch's input starts a knob's width to the left, so it covers the whole track.
  "input.MuiSwitch-input[type='checkbox']:not(:checked), input.MuiSwitch-input[type='checkbox']:checked":
    { left: '-100%' },
};

/** Splits a selector list on its top-level commas, leaving the ones inside `:is(…)` alone. */
function splitSelectors(key: string): string[] {
  const selectors: string[] = [];
  let depth = 0;
  let current = '';

  for (const character of key) {
    if (character === ',' && depth === 0) {
      selectors.push(current.trim());
      current = '';
      continue;
    }
    if (character === '(') {
      depth += 1;
    }
    if (character === ')') {
      depth -= 1;
    }
    current += character;
  }
  selectors.push(current.trim());

  return selectors;
}

/**
 * The same declarations under each of `roots`, for a portaled overlay.
 *
 * Each selector of a multi-selector key is scoped in its own right. Wrapping the roots around the
 * whole key would scope only the first and leave the rest global — an island's reset landing on
 * the Knockout page around it.
 */
function scopedTo(roots: string[], styles: Record<string, object>): Record<string, object> {
  return Object.fromEntries(
    Object.entries(styles).map(([key, declarations]) => [
      splitSelectors(key)
        .flatMap((selector) => roots.map((root) => `${root} ${selector}`))
        .join(', '),
      declarations,
    ])
  );
}

const HOST_FORM_RESET = {
  '& input, & textarea': FIELD_RESET,
  '& input:focus, & textarea:focus': {
    borderBottomStyle: 'none',
    boxShadow: 'none',
    outlineStyle: 'none',
  },
  ...scopedTo(['&'], INPUT_PADDING),
  ...scopedTo(['&'], INPUT_FOCUSED_BORDER),
  ...scopedTo(['&'], TICK_BOX_OFFSET),
};

/**
 * The same reset for MUI's overlays, which the island cannot reach any other way.
 *
 * A `Menu`, `Popover`, `Select` dropdown or `Tooltip` is portaled to `document.body` — outside the
 * island's element, so `ScopedCssBaseline` above does not wrap it and the host styles it unopposed.
 * That is how the filter panel's value field kept its underline after the in-island fields were
 * fixed. Scoped to MUI's own overlay roots, which exist only because this React tree created them,
 * so nothing on the Knockout page is touched.
 */
const OVERLAY_ROOTS = [
  '.MuiPopover-root',
  '.MuiMenu-root',
  '.MuiDialog-root',
  '.MuiAutocomplete-popper',
  '.MuiTooltip-popper',
];

const overlayStyles = {
  ...scopedTo(OVERLAY_ROOTS, INPUT_PADDING),
  ...scopedTo(OVERLAY_ROOTS, INPUT_FOCUSED_BORDER),
  ...scopedTo(OVERLAY_ROOTS, TICK_BOX_OFFSET),
  [OVERLAY_ROOTS.map((root) => `${root} :is(input, textarea)`).join(', ')]: FIELD_RESET,
};

const HOST_DIALOG = 'qt-popup';

/**
 * MUI portals a list, tooltip, menu or dialog to `document.body` at z-index 1300–1500, under any
 * host dialog, so inside one they open behind it. Opened in the dialog instead, they stack above
 * its content, and whatever the host puts above that dialog (an error dialog) still covers them.
 *
 * Popover is listed beside Modal because it hands Modal its anchor's `document.body` itself.
 */
function overlaysInHostDialog(host: HTMLElement) {
  const container = () => host.closest(HOST_DIALOG) ?? document.body;

  return {
    MuiPopper: { defaultProps: { container } },
    MuiPopover: { defaultProps: { container } },
    MuiModal: { defaultProps: { container } },
  };
}

function buildIslandTheme(host: HTMLElement): Theme {
  const primary = window.APP_CONFIG?.PRIMARY_COLOR;
  // The Next.js theme knows only QuickTrials' colour.
  const palette = primary
    ? { primary: theme.palette.augmentColor({ color: { main: primary } }) }
    : {};

  return createTheme(theme, { components: overlaysInHostDialog(host), palette });
}

/**
 * `_app.tsx`'s provider stack minus what is Next-specific or owned by the Knockout host. Two
 * non-obvious choices: the *named* `AxiosProvider`, because the default export is wrapped in
 * `withUser()`; and `ScopedCssBaseline`, without which materialize.css's bare-element selectors
 * style MUI's internals unopposed. A map island must add `APIProvider` itself — it needs a key.
 * `host` is the element the island is mounted into, which places its overlays.
 */
export function IslandRoot({ host, children }: { host: HTMLElement; children: ReactNode }) {
  const islandTheme = useMemo(() => buildIslandTheme(host), [host]);

  return (
    <ThemeProvider theme={islandTheme}>
      <I18nProvider i18n={i18n}>
        <DataGridLocalizationProvider>
          <ServerInfoProvider initialServerInfo={SERVER_INFO}>
            <AuthProvider>
              <WhenSessionRestored>
                <AxiosProvider>
                  <QueryClientProvider client={queryClient}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <GlobalStyles styles={overlayStyles} />
                      <ScopedCssBaseline sx={HOST_FORM_RESET}>{children}</ScopedCssBaseline>
                    </LocalizationProvider>
                  </QueryClientProvider>
                </AxiosProvider>
              </WhenSessionRestored>
            </AuthProvider>
          </ServerInfoProvider>
        </DataGridLocalizationProvider>
      </I18nProvider>
    </ThemeProvider>
  );
}
